import React from 'react';
import { CustomFieldType, CustomFieldUtil } from '../../../../types/custom_field';
import { FileEntity } from '../../../../types/file';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { toMap } from '../../../../util/map.util';
import { toFilterString } from '../../../../util/string.util';
import { TableHeader } from '../Table';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Company } from '../../../../types/company';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { ProductMasterDataTableSettingsColumn } from '../../../../types/table_settings/pmd/ProductMasterDataTableSettings';
import dayjs from 'dayjs';

export const productMasterDataTableHeaders = (
  settings: TableSettings<any>,
  files: Map<string, FileEntity>,
  companies: Map<string, Company>,
  company: Company,
  locale: string,
) => {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<ProductMasterData>[] = [];

  const isPartner = company.partner;

  if (isPartner) {
    columns.push({
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: ProductMasterData) => companies.get(item.companyId)?.name || '',
    });
  }

  if (wantedColumns.has(ProductMasterDataTableSettingsColumn.productName)) {
    columns.push({
      key: 'name',
      name: wantedColumns.get(ProductMasterDataTableSettingsColumn.productName)?.label || '',
      text: (item: ProductMasterData) => item.productName,
      column: ProductMasterDataTableSettingsColumn.productName,
    });
  }

  if (wantedColumns.has(ProductMasterDataTableSettingsColumn.productNumber)) {
    columns.push({
      key: 'number',
      name: wantedColumns.get(ProductMasterDataTableSettingsColumn.productNumber)?.label || '',
      text: (item: ProductMasterData) => item.productNumber,
      column: ProductMasterDataTableSettingsColumn.productNumber,
    });
  }

  if (wantedColumns.has(ProductMasterDataTableSettingsColumn.files)) {
    columns.push({
      key: 'files',
      name: wantedColumns.get(ProductMasterDataTableSettingsColumn.files)?.label || '',
      text: (item: ProductMasterData) => {
        const count = [...files.values()].filter(f => f.entityId === item.id).length;
        if (!count) return '';

        return (
          <div className='flex items-center gap-2'>
            <p>{count.toString()}</p>
            <AttachFileIcon sx={{ width: 16, height: 16, mb: 0.2 }} />
          </div>
        );
      },
      column: ProductMasterDataTableSettingsColumn.files,
    });
  }

  if (wantedColumns.has(ProductMasterDataTableSettingsColumn.ean)) {
    columns.push({
      key: 'ean',
      name: wantedColumns.get(ProductMasterDataTableSettingsColumn.ean)?.label || '',
      text: (item: ProductMasterData) => item.globalTradeItemNumber || '',
      column: ProductMasterDataTableSettingsColumn.ean,
    });
  }

  const defaultKeys = new Set(Object.keys(ProductMasterDataTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  customFieldColumns.forEach(item => {
    columns.push({
      key: item.column,
      name: item.label,
      sortValue: (pmd: ProductMasterData) => {
        const customFieldValue = pmd.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      text: (o: ProductMasterData) => {
        const customFieldValue = o.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      column: item.column,
    });
  });

  return columns.sort(
    (a: TableHeader<ProductMasterData>, b: TableHeader<ProductMasterData>) =>
      (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  );
};

export const productMasterDataTableFilter = (
  item: ProductMasterData,
  filter: string,
  settings: TableSettings<any> | undefined,
) => {
  const filterString = toFilterString(filter);
  const wantedColumns = toMap(settings?.columns || [], 'column');

  if (!filter) return true;

  if (
    wantedColumns.has(ProductMasterDataTableSettingsColumn.productName) &&
    item.productName.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ProductMasterDataTableSettingsColumn.productNumber) &&
    item.productNumber.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ProductMasterDataTableSettingsColumn.ean) &&
    item.globalTradeItemNumber?.toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  const defaultKeys = new Set(Object.keys(ProductMasterDataTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  for (const custom of customFieldColumns) {
    const field = toFilterString(item.customFieldValues().find(i => i.name === custom.column)?.value);
    if (field?.includes(filterString)) return true;
  }

  return false;
};
